// §404(a)(3) employer-deduction SCREEN for defined-contribution allocations.
//
// A screen, never a computed tax deduction or a deduction opinion: it compares
// deductible-type employer contributions (nonelective + match + profit
// sharing + QNEC — elective deferrals are separately deductible as
// compensation, catch-ups and reallocated forfeitures are not employer cost)
// against 25% of the aggregate compensation otherwise paid to the plan's
// eligible participants for the year (§404(a)(3)(A)(i)).
//
// Population: participants eligible to benefit under the plan (or actually
// benefiting), each capped at §401(a)(17). Excluded people and their
// compensation are reported, not silently dropped. All math in integer cents.

import { makeAssumption, makeComplianceStatus, makeLimitation, makeReason } from './complianceStatus.js'

export const DEDUCTION_404_LIMIT_PERCENT = 25
export const DEDUCTION_404_CITATION = 'IRC §404(a)(3)'
export const DEDUCTION_404A7_CITATION = 'IRC §404(a)(7)'

// `participants`: normalized rows from newCompInputs (cents fields +
// deductionCompensation dollars). `proposedProfitSharingCentsById`: the
// scenario's PS dollars, keyed by participant id.
export function evaluateDeduction404Screen(participants = [], {
  proposedProfitSharingCentsById = {},
  limitPercent = DEDUCTION_404_LIMIT_PERCENT,
} = {}) {
  const rows = Array.isArray(participants) ? participants.filter(Boolean) : []
  const included = []
  const excluded = []
  for (const row of rows) {
    const eligible = row.eligibilityStatus === 'eligible' || row.benefitsUnderPlan === true
      || (proposedProfitSharingCentsById[row.id] ?? 0) > 0
    if (eligible) included.push(row)
    else excluded.push(row)
  }
  if (!included.length) {
    return {
      ...makeComplianceStatus({
        status: 'unavailable',
        reasons: [makeReason('no-eligible-participants', 'No eligible participants were found for the §404 compensation base.')],
      }),
      limitPercent,
      citation: DEDUCTION_404_CITATION,
      includedCount: 0,
      excludedCount: excluded.length,
      includedCompensationCents: 0,
      excludedCompensationCents: 0,
      limitCents: 0,
      employerContributionCents: 0,
      headroomCents: 0,
      bySource: {},
    }
  }

  const cents = (dollars) => Math.round((Number(dollars) || 0) * 100)
  const includedCompensationCents = included.reduce((sum, row) => sum + cents(row.deductionCompensation), 0)
  const excludedCompensationCents = excluded.reduce((sum, row) => sum + cents(row.deductionCompensation), 0)
  const limitCents = Math.floor(includedCompensationCents * (Math.max(0, Number(limitPercent) || 0) / 100))

  const bySource = { nonelective: 0, safeHarborNonelective: 0, match: 0, qnec: 0, profitSharing: 0 }
  for (const row of included) {
    bySource.nonelective += row.otherNonelectiveCents || 0
    bySource.safeHarborNonelective += row.safeHarborNonelectiveCents || 0
    bySource.match += row.matchCents || 0
    bySource.qnec += row.qnecCents || 0
    bySource.profitSharing += Math.max(0, Math.round(Number(proposedProfitSharingCentsById[row.id]) || 0))
  }
  const employerContributionCents = Object.values(bySource).reduce((sum, value) => sum + value, 0)
  const headroomCents = limitCents - employerContributionCents
  const passes = employerContributionCents <= limitCents

  return {
    ...makeComplianceStatus({
      status: passes ? 'passes' : 'fails',
      reasons: passes ? [] : [makeReason('over-404-limit',
        `Employer contributions exceed ${limitPercent}% of eligible-participant compensation by $${((-headroomCents) / 100).toFixed(2)}.`)],
      limitations: [makeLimitation('deduction-screen',
        'Directional §404(a)(3) screen on entered compensation; not a tax deduction computation or opinion. Actual deductibility depends on the employer’s tax year, fiscal facts, and any combined-plan limits.')],
    }),
    limitPercent,
    citation: DEDUCTION_404_CITATION,
    includedCount: included.length,
    excludedCount: excluded.length,
    includedIds: included.map((row) => row.id),
    excludedIds: excluded.map((row) => row.id),
    includedCompensationCents,
    excludedCompensationCents,
    limitCents,
    employerContributionCents,
    headroomCents,
    bySource,
  }
}

// ---------------------------------------------------------------------------
// §404(a)(7) combined DB/DC deduction SCREEN.
//
// When an employer maintains a DB and a DC plan with at least one common
// beneficiary, the combined deduction can be limited to the greater of 25% of
// aggregate compensation or the DB minimum required contribution — but DC
// employer contributions up to 6% of compensation are carved out, elective
// deferrals never count, and PBGC-covered DB plans are exempt. The app cannot
// compute the DB minimum required contribution (§430 funding needs an
// actuary), so this screen determines APPLICABILITY honestly:
//
//   not-applicable   — no cash-balance credits, or no overlapping beneficiary.
//   passes           — the combination exists but is not triggered (DC
//                      employer money within the 6% carve-out, or the DB plan
//                      is affirmatively PBGC-covered).
//   requires-review  — triggered, or PBGC coverage unknown with DC money over
//                      6%: the combined limit depends on the DB minimum
//                      required contribution only an actuary can supply.
//
// It never claims a hard failure: the greater-of limb cannot be evaluated
// from the data the app has, and inventing it would be worse than saying so.
// ---------------------------------------------------------------------------

export function evaluateCombinedDeduction404a7Screen(participants = [], {
  proposedProfitSharingCentsById = {},
  cashBalanceCreditCentsById = {},
  dbPbgcCovered = 'unknown', // 'yes' | 'no' | 'unknown'
} = {}) {
  const rows = Array.isArray(participants) ? participants.filter(Boolean) : []
  const cents = (dollars) => Math.round((Number(dollars) || 0) * 100)
  const cbCentsFor = (row) => Math.max(0, Math.round(Number(cashBalanceCreditCentsById[row.id]) || 0))
  const psCentsFor = (row) => Math.max(0, Math.round(Number(proposedProfitSharingCentsById[row.id]) || 0))
  const dcEmployerCentsFor = (row) => (row.otherNonelectiveCents || 0)
    + (row.safeHarborNonelectiveCents || 0) + (row.matchCents || 0)
    + (row.qnecCents || 0) + psCentsFor(row)

  const anyDb = rows.some((row) => cbCentsFor(row) > 0)
  const overlapping = rows.filter((row) => cbCentsFor(row) > 0 && dcEmployerCentsFor(row) > 0)
  if (!anyDb || !overlapping.length) {
    return {
      ...makeComplianceStatus({ status: 'not-applicable' }),
      citation: DEDUCTION_404A7_CITATION,
      applicable: false,
      overlappingCount: overlapping.length,
      dcEmployerContributionCents: 0,
      sixPercentCarveOutCents: 0,
      excessOverCarveOutCents: 0,
      dbCreditCents: rows.reduce((sum, row) => sum + cbCentsFor(row), 0),
      dbPbgcCovered,
    }
  }

  const included = rows.filter((row) => row.eligibilityStatus === 'eligible'
    || row.benefitsUnderPlan === true || psCentsFor(row) > 0 || cbCentsFor(row) > 0)
  const compensationCents = included.reduce((sum, row) => sum + cents(row.deductionCompensation), 0)
  const dcEmployerContributionCents = included.reduce((sum, row) => sum + dcEmployerCentsFor(row), 0)
  const dbCreditCents = included.reduce((sum, row) => sum + cbCentsFor(row), 0)
  const sixPercentCarveOutCents = Math.floor(compensationCents * 0.06)
  const excessOverCarveOutCents = Math.max(0, dcEmployerContributionCents - sixPercentCarveOutCents)
  const twentyFivePercentCents = Math.floor(compensationCents * 0.25)

  if (dbPbgcCovered === 'yes') {
    return {
      ...makeComplianceStatus({
        status: 'passes',
        assumptions: [makeAssumption('pbgc-covered', 'DB plan attested as PBGC-covered (§404(a)(7) exemption)', true, { source: 'user', estimated: false })],
        limitations: [makeLimitation('deduction-screen', 'Directional §404(a)(7) applicability screen; not a tax deduction computation or opinion.')],
      }),
      citation: DEDUCTION_404A7_CITATION,
      applicable: true,
      triggered: false,
      overlappingCount: overlapping.length,
      compensationCents,
      dcEmployerContributionCents,
      dbCreditCents,
      sixPercentCarveOutCents,
      excessOverCarveOutCents,
      twentyFivePercentCents,
      dbPbgcCovered,
    }
  }

  const triggered = excessOverCarveOutCents > 0
  return {
    ...makeComplianceStatus({
      status: triggered ? 'requires-review' : 'passes',
      reasons: triggered
        ? [makeReason('combined-deduction-triggered',
            `DC employer contributions of $${(dcEmployerContributionCents / 100).toFixed(2)} exceed the 6% carve-out ($${(sixPercentCarveOutCents / 100).toFixed(2)}) with an overlapping DB plan; the §404(a)(7) combined limit (greater of 25% of pay or the DB minimum required contribution) applies and needs the plan actuary's §430 figure.`)]
        : [],
      limitations: [
        makeLimitation('deduction-screen', 'Directional §404(a)(7) applicability screen; not a tax deduction computation or opinion.'),
        ...(dbPbgcCovered === 'unknown'
          ? [makeLimitation('pbgc-unknown', 'PBGC coverage of the DB plan is unanswered; if covered, the §404(a)(7) combined limit does not apply.')]
          : []),
      ],
    }),
    citation: DEDUCTION_404A7_CITATION,
    applicable: true,
    triggered,
    overlappingCount: overlapping.length,
    compensationCents,
    dcEmployerContributionCents,
    dbCreditCents,
    sixPercentCarveOutCents,
    excessOverCarveOutCents,
    twentyFivePercentCents,
    dbPbgcCovered,
  }
}
